/**
 * GeoJSON parser for flight tracks. Accepts:
 *   1. A FeatureCollection of Point features \u2014 uses per-feature `time` /
 *      `altitude` / `speed` / `heading` properties.
 *   2. A Feature with a LineString geometry \u2014 uses `coordinates` array;
 *      time stamps may be in `properties.coordTimes: string[]` (Mapbox
 *      convention) or `properties.times: number[]`.
 *   3. A bare LineString or MultiLineString geometry.
 */
import type { TrackPoint } from './track-types';

const M_TO_FT = 3.28084;
const MPS_TO_KTS = 1.94384;

type Coord = [number, number] | [number, number, number];

interface FeatureLike {
  type?: string;
  geometry?: GeomLike;
  properties?: Record<string, unknown>;
}

interface GeomLike {
  type: string;
  coordinates: unknown;
}

function coordToPoint(c: Coord, timestamp?: number): TrackPoint | null {
  const lon = c[0];
  const lat = c[1];
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const altM = c[2];
  return {
    latitude: lat,
    longitude: lon,
    altitudeFt: Number.isFinite(altM as number) ? (altM as number) * M_TO_FT : undefined,
    timestamp,
  };
}

function parseTimeProp(v: unknown): number | undefined {
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function numProp(props: Record<string, unknown> | undefined, keys: string[]): number | undefined {
  if (!props) return undefined;
  for (const k of keys) {
    const v = props[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function pointsFromLineString(coords: Coord[], timesProp: unknown): TrackPoint[] {
  let times: (number | undefined)[] = [];
  if (Array.isArray(timesProp)) {
    times = timesProp.map((t) => parseTimeProp(t));
  }
  const out: TrackPoint[] = [];
  coords.forEach((c, i) => {
    const p = coordToPoint(c, times[i]);
    if (p) out.push(p);
  });
  return out;
}

export function parseGeoJson(content: string): { name: string | undefined; points: TrackPoint[] } {
  let json: unknown;
  try { json = JSON.parse(content); } catch (e) {
    throw new Error('GeoJSON parse error: ' + (e as Error).message);
  }
  const root = json as FeatureLike & { features?: FeatureLike[]; name?: string };
  const points: TrackPoint[] = [];
  let name: string | undefined = root.name;

  if (root.type === 'FeatureCollection' && Array.isArray(root.features)) {
    // Case 1: FeatureCollection of Points \u2014 one point per feature
    const allPoints = root.features.every((f) => f.geometry?.type === 'Point');
    if (allPoints) {
      for (const f of root.features) {
        const coords = f.geometry!.coordinates as Coord;
        const time = parseTimeProp(f.properties?.time ?? f.properties?.timestamp);
        const alt = numProp(f.properties, ['altitude_ft', 'alt_ft']);
        const altM = numProp(f.properties, ['altitude_m', 'alt_m', 'altitude']);
        const speedKts = numProp(f.properties, ['ground_speed_kts', 'speed_kts', 'gs']);
        const speedMps = numProp(f.properties, ['speed_mps', 'gs_mps']);
        const heading = numProp(f.properties, ['heading', 'course', 'track', 'bearing']);
        const p = coordToPoint(coords, time);
        if (!p) continue;
        if (alt !== undefined) p.altitudeFt = alt;
        else if (altM !== undefined) p.altitudeFt = altM * M_TO_FT;
        if (speedKts !== undefined) p.groundSpeedKts = speedKts;
        else if (speedMps !== undefined) p.groundSpeedKts = speedMps * MPS_TO_KTS;
        if (heading !== undefined) p.heading = heading;
        points.push(p);
      }
      return { name, points };
    }
    // Case 2: FeatureCollection where one feature is a LineString
    for (const f of root.features) {
      if (f.geometry?.type === 'LineString') {
        const times = f.properties?.coordTimes ?? f.properties?.times;
        points.push(...pointsFromLineString(f.geometry.coordinates as Coord[], times));
        name = name ?? (f.properties?.name as string | undefined);
      }
    }
    return { name, points };
  }

  if (root.type === 'Feature' && root.geometry?.type === 'LineString') {
    const times = root.properties?.coordTimes ?? root.properties?.times;
    points.push(...pointsFromLineString(root.geometry.coordinates as Coord[], times));
    name = name ?? (root.properties?.name as string | undefined);
    return { name, points };
  }

  if (root.type === 'LineString' && Array.isArray((root as GeomLike).coordinates)) {
    points.push(...pointsFromLineString((root as GeomLike).coordinates as Coord[], undefined));
    return { name, points };
  }

  if (root.type === 'MultiLineString' && Array.isArray((root as GeomLike).coordinates)) {
    for (const line of (root as GeomLike).coordinates as Coord[][]) {
      points.push(...pointsFromLineString(line, undefined));
    }
    return { name, points };
  }

  throw new Error('GeoJSON does not contain a recognised track geometry');
}
